import { useEffect, useRef, useState, type RefObject } from 'react';

// Permite arrastar horizontalmente um container scrollável clicando fora dos
// elementos marcados com [data-pan-skip] (cards, colunas, botões).
// Cursor: grab default, grabbing durante o arrasto. Use também
// `select-none` no container para evitar seleção de texto.
export function useDragPan<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const dragRef = useRef({ startX: 0, startScroll: 0 });
  const [isDragging, setIsDragging] = useState(false);

  function onMouseDown(e: React.MouseEvent<T>) {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('[data-pan-skip]')) return;
    const el = ref.current;
    if (!el) return;
    dragRef.current = { startX: e.clientX, startScroll: el.scrollLeft };
    setIsDragging(true);
    e.preventDefault();
  }

  useEffect(() => {
    if (!isDragging) return undefined;
    function onMove(e: MouseEvent) {
      const el = ref.current;
      if (!el) return;
      el.scrollLeft = dragRef.current.startScroll - (e.clientX - dragRef.current.startX);
    }
    function onUp() {
      setIsDragging(false);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging]);

  return {
    ref: ref as RefObject<T>,
    isDragging,
    onMouseDown,
    cursorClass: isDragging ? 'cursor-grabbing' : 'cursor-grab',
  };
}
